/**
 * Activity Icon Mapper
 * Maps activity labels to React Icons
 * Provides fallback icons for common travel activities
 * Uses Font Awesome and Material Design icons from react-icons
 */

import React from 'react';
import {
  FaPlane,
  FaTrain,
  FaBus,
  FaCar,
  FaShip,
  FaMonument,
  FaChurch,
  FaBuilding,
  FaMoon,
  FaStar,
  FaMusic,
  FaBook,
  FaBicycle,
  FaCompass,
  FaMap,
  FaWalking,
  FaGamepad,
  FaPalette,
  FaFilm,
  FaMagic,
  FaGift,
  FaTrophy,
  FaAward,
  FaCoffee,
  FaWineGlass,
  FaShieldAlt,
  FaUserShield,
  FaUserCheck,
  FaUserFriends,
  FaUserTie,
  FaHome,
  FaLock,
  FaCheckCircle,
  FaHeart,
  FaHandshake,
  FaBroom,
  FaChild,
  FaVenus,
  FaTransgender,
  FaWheelchair,
} from 'react-icons/fa';
import { MdTempleBuddhist } from 'react-icons/md';
import type { IconType } from 'react-icons';
import {
  Coins,
  CloudSun,
  Camera,
  ShoppingBag,
  Store,
  Utensils,
  Waves,
  Dumbbell,
  Activity,
  MapPin,
  Navigation,
  Zap,
  CreditCard,
  TreePine,
  AlertCircle,
  Lightbulb,
  Footprints,
  Users,
  Star,
} from 'lucide-react';

export interface IconMapping {
  [key: string]:
    | IconType
    | React.ComponentType<{
        size?: number;
        className?: string;
        color?: string;
      }>
    | string; // Allow SVG paths as strings
}

/**
 * Primary mapping of activity labels to Lucide icons
 * Keys are normalized (lowercase, trimmed)
 */
const activityIconMap: IconMapping = {
  // Transportation
  flight: FaPlane,
  plane: FaPlane,
  airplane: FaPlane,
  air: FaPlane,
  train: FaTrain,
  railway: FaTrain,
  bus: FaBus,
  car: FaCar,
  drive: FaCar,
  driving: FaCar,
  boat: FaShip,
  ship: FaShip,
  cruise: FaShip,
  ferry: FaShip,
  'boat ride': FaShip,
  'boat rides': FaShip,
  sailing: FaShip,
  cycling: FaBicycle,
  bike: FaBicycle,
  bicycle: FaBicycle,
  biking: FaBicycle,
  caravan: FaCar,
  motorcycle: '/assets/images/icons/motorcycle.svg',
  'motorcycle ride': '/assets/images/icons/motorcycle.svg',
  'bike ride': '/assets/images/icons/motorcycle.svg',

  // Activities - using Lucide icons
  photography: Camera,
  photo: Camera,
  camera: Camera,
  shopping: ShoppingBag,
  'temple visits': MdTempleBuddhist,
  temple: MdTempleBuddhist,
  'temple visit': MdTempleBuddhist,
  pilgrimage: MdTempleBuddhist,
  'pilgrimage tours': MdTempleBuddhist,
  museum: FaBuilding,
  museums: FaBuilding,
  monument: FaMonument,
  monuments: FaMonument,
  'museums & monuments': FaBuilding,
  hiking: '/assets/images/icons/trekking.svg',
  trekking: '/assets/images/icons/trekking.svg',
  trek: '/assets/images/icons/trekking.svg',
  camping: '/assets/images/icons/campfire.svg',
  camp: '/assets/images/icons/campfire.svg',
  swimming: Waves,
  swim: Waves,
  'holy dip': Waves,
  'holy dip in ganges': Waves,
  water: Waves,
  'water activities': Waves,
  'ghat exploration': MapPin,
  ghat: MapPin,
  nature: '/assets/images/icons/nature.svg',
  'nature views': '/assets/images/icons/nature.svg',
  'good for nature views': '/assets/images/icons/nature.svg',
  wildlife: '/assets/images/icons/wildlife.svg',
  'dense forest': TreePine,
  forest: '/assets/images/icons/nature.svg',
  mountain: '/assets/images/icons/mountains.svg',
  mountains: '/assets/images/icons/mountains.svg',
  'mountain views': '/assets/images/icons/mountains.svg',
  adventure: Activity,
  'adventure activities': Activity,
  sports: '/assets/images/icons/sports.svg',
  fitness: Dumbbell,
  gym: Dumbbell,
  workout: Dumbbell,
  yoga: FaMagic,
  meditation: FaMagic,
  'yoga and meditation': FaMagic,
  wellness: FaHeart,
  spa: FaHeart,
  relaxation: FaHeart,

  // Cultural & Entertainment
  culture: FaPalette,
  'cultural performances': FaFilm,
  'cultural experience': FaFilm,
  music: FaMusic,
  concert: FaMusic,
  festival: FaMagic,
  celebration: FaMagic,
  art: FaPalette,
  gallery: FaPalette,
  theater: FaFilm,
  dance: FaMusic,
  performance: FaFilm,
  show: FaFilm,
  entertainment: FaGamepad,
  games: FaGamepad,

  // Food & Dining - using Lucide icons
  food: Utensils,
  dining: Utensils,
  restaurant: Utensils,
  restaurants: Utensils,
  cuisine: Utensils,
  'local food': Utensils,
  'food specialties': Utensils,
  'street food': Utensils,
  cafe: FaCoffee,
  coffee: FaCoffee,
  bar: FaWineGlass,
  drinks: FaWineGlass,
  nightlife: FaMoon,

  // Sightseeing & Exploration - using Lucide icons
  sightseeing: MapPin,
  explore: FaCompass,
  exploration: FaCompass,
  tour: FaMap,
  'city tour': FaMap,
  'guided tour': '/assets/images/icons/guided.svg',
  guide: '/assets/images/icons/guided.svg',
  guided: '/assets/images/icons/guided.svg',
  'local guides': FaUserFriends,
  walking: FaWalking,
  walk: Navigation,
  'walking tour': FaWalking,
  visit: MapPin,
  'must visit': FaStar,
  attraction: FaStar,
  attractions: FaStar,
  'places to visit': MapPin,
  landmark: FaMonument,
  historical: FaBook,
  history: FaBook,
  'historical significance': FaBook,
  heritage: FaMonument,

  // Religious & Spiritual
  church: FaChurch,
  mosque: MdTempleBuddhist,
  'holy place': MdTempleBuddhist,
  spiritual: FaMagic,
  prayer: FaMagic,
  worship: MdTempleBuddhist,
  religious: MdTempleBuddhist,

  // Shopping & Services - using Lucide icons
  'shopping for silk sarees': ShoppingBag,
  'silk shopping': ShoppingBag,
  market: Store,
  'local market': Store,
  bazaar: Store,
  souvenirs: FaGift,
  'shopping area': ShoppingBag,

  // General - using Lucide icons
  experience: FaMagic,
  'local experiences': FaMagic,
  activity: Zap,
  activities: Zap,
  'things to do': Zap,
  'must do': FaStar,
  popular: FaStar,
  recommended: FaStar,
  'top attractions': FaTrophy,
  'best of': FaAward,
  // Nearby items - using Lucide icons
  'activities nearby': Activity,
  'nearby activities': Activity,
  'nearby attractions': Star,
  'attractions nearby': Star,

  // Overview/Stats items - using SVG assets from icons folder
  population: '/assets/images/icons/population.svg',
  people: '/assets/images/icons/population.svg',
  weather: CloudSun,
  temperature: CloudSun,
  temp: CloudSun,
  sunrise: '/assets/images/icons/sunrise.svg',
  'sun rise': '/assets/images/icons/sunrise.svg',
  sunset: '/assets/images/icons/sunset.svg',
  'sun set': '/assets/images/icons/sunset.svg',
  currency: Coins,
  money: Coins,
  rupee: Coins,
  'have money available': CreditCard, // Uses ATM icon (CreditCard)
  'money available': Coins,
  atm: CreditCard, // ATM uses CreditCard icon
  atms: CreditCard,
  'lakes, ponds': '/assets/images/icons/lakes.svg',
  lakes: '/assets/images/icons/lakes.svg',
  ponds: '/assets/images/icons/lakes.svg',
  river: Waves,
  rivers: Waves,
  timezone: '/assets/images/icons/timezone.svg',
  'time zone': '/assets/images/icons/timezone.svg',
  clock: '/assets/images/icons/timezone.svg',
  time: '/assets/images/icons/timezone.svg',
  humidity: '/assets/images/icons/humidity.svg',
  'air pressure': '/assets/images/icons/pressure.svg',
  pressure: '/assets/images/icons/pressure.svg',
  'air quality': CloudSun,
  quality: CloudSun,

  // Emergency contacts - using SVG assets from icons folder
  police: '/assets/images/icons/police.svg',
  cop: '/assets/images/icons/police.svg',
  ambulance: '/assets/images/icons/ambulance.svg',
  medical: '/assets/images/icons/ambulance.svg',
  hospital: '/assets/images/icons/ambulance.svg',
  'fire brigade': '/assets/images/icons/fire.svg',
  fire: '/assets/images/icons/fire.svg',
  'fire department': '/assets/images/icons/fire.svg',
  emergency: AlertCircle,
  contact: AlertCircle,
  'emergency contact': AlertCircle,

  // Safety & Security - Each keyword has a unique icon
  'child friendly': FaChild,
  'child safety': FaChild,
  'women safe': FaVenus,
  'women travel safety': FaVenus,
  'lgbtq comforts': FaTransgender,
  'disability friendly': FaWheelchair,
  'group tours': FaUserFriends, // Group of friends icon
  'clean locations': FaBroom, // Broom for cleanliness
  'no danger zones': FaCheckCircle, // Check mark for safe
  'safe zones': FaHome, // Home icon for safe zone
  'low crime rates': FaShieldAlt, // Shield for security
  'very safe': FaCheckCircle, // Check mark for verified safe
  'security measures taken': FaLock, // Lock for security
  'personal safety': FaUserShield, // User with shield
  'traveler advisories': FaUserCheck, // User with check
  'emergency evacuation': AlertCircle, // Emergency icon
  'local friendly': FaHandshake, // Handshake for friendly
  'family safe': FaHeart, // Heart for family
  'safe zone': FaHome, // Home for safe zone
  'clean location': FaBroom, // Broom for clean
  'supervised area': FaUserTie, // Professional/staff icon
  'emergency services': AlertCircle, // Emergency Services icon
  'staff onsite': FaUserTie, // Professional/staff icon
  'staff on-site': FaUserTie, // Professional/staff icon

  // Lighting & Area Characteristics - using Lucide icons
  'well lit': Lightbulb,
  'well-lit': Lightbulb,
  well_lit: Lightbulb,
  lighting: Lightbulb,
  'good lighting': Lightbulb,
  'busy area': Users,
  'busy-area': Users,
  busy_area: Users,
  crowded: Users,
  'pedestrian only': Footprints,
  'pedestrian-only': Footprints,
  pedestrian_only: Footprints,
  'pedestrian zone': Footprints,
  'walking zone': Footprints,
  'walking only': Footprints,
};

/**
 * Secondary mapping for partial matches and variations
 */
const partialMatchMap: Array<{
  keywords: string[];
  icon:
    | IconType
    | React.ComponentType<{
        size?: number;
        className?: string;
        color?: string;
      }>
    | string; // Allow SVG paths as strings
}> = [
  { keywords: ['dip', 'bath', 'bathing'], icon: Waves },
  { keywords: ['ghat', 'river', 'ganges'], icon: Waves },
  { keywords: ['temple', 'shrine', 'mandir'], icon: MdTempleBuddhist },
  { keywords: ['museum', 'gallery', 'exhibition'], icon: FaBuilding },
  {
    keywords: ['hike', 'trek', 'trekking', 'hiking', 'climb'],
    icon: '/assets/images/icons/trekking.svg',
  },
  { keywords: ['swim', 'water', 'beach'], icon: Waves },
  { keywords: ['shop', 'buy', 'purchase'], icon: ShoppingBag },
  { keywords: ['eat', 'dine', 'food', 'meal'], icon: Utensils },
  { keywords: ['photo', 'picture', 'capture'], icon: Camera },
  { keywords: ['culture', 'tradition', 'heritage'], icon: FaPalette },
  { keywords: ['adventure', 'outdoor'], icon: Activity },
  { keywords: ['sport', 'sports'], icon: '/assets/images/icons/sports.svg' },
  { keywords: ['theater', 'performance', 'show', 'drama'], icon: FaFilm },
  { keywords: ['nature'], icon: '/assets/images/icons/nature.svg' },
  { keywords: ['dense forest'], icon: TreePine },
  { keywords: ['forest'], icon: '/assets/images/icons/nature.svg' },
  {
    keywords: ['wildlife', 'wild life'],
    icon: '/assets/images/icons/wildlife.svg',
  },
  {
    keywords: ['mountain', 'hill', 'peak'],
    icon: '/assets/images/icons/mountains.svg',
  },
  { keywords: ['music', 'concert', 'festival'], icon: FaMusic },
  { keywords: ['yoga', 'meditation', 'wellness'], icon: FaMagic },
  {
    keywords: ['weather', 'forecast'],
    icon: CloudSun,
  },
  {
    keywords: ['temperature', 'temp'],
    icon: CloudSun,
  },
  {
    keywords: ['humidity', 'moisture'],
    icon: '/assets/images/icons/humidity.svg',
  },
  {
    keywords: ['pressure', 'air pressure', 'atmospheric'],
    icon: '/assets/images/icons/pressure.svg',
  },
  {
    keywords: ['money', 'currency', 'available', 'atm', 'rupee'],
    icon: Coins,
  },
  {
    keywords: ['lake', 'pond', 'lakes'],
    icon: '/assets/images/icons/lakes.svg',
  },
  { keywords: ['water body'], icon: Waves },
  { keywords: ['child', 'children'], icon: FaChild },
  { keywords: ['family'], icon: FaHeart },
  { keywords: ['friendly'], icon: FaHandshake },
  { keywords: ['women', 'woman', 'female', 'safe'], icon: FaVenus },
  { keywords: ['lgbtq', 'lgbt', 'comfort'], icon: FaTransgender },
  { keywords: ['disability', 'disabled', 'accessible'], icon: FaWheelchair },
  {
    keywords: ['safety', 'security', 'secure', 'shield', 'protection'],
    icon: FaShieldAlt,
  },
  { keywords: ['safe'], icon: FaCheckCircle },
  { keywords: ['danger', 'zone', 'crime', 'advisory'], icon: FaLock },
  {
    keywords: [
      'evacuation',
      'emergency',
      'emergency contact',
      'emergency services',
    ],
    icon: AlertCircle, // Emergency Services icon
  },
  { keywords: ['local'], icon: FaHandshake },
  { keywords: ['group'], icon: FaUserFriends },
  { keywords: ['guided', 'guide'], icon: '/assets/images/icons/guided.svg' },
  { keywords: ['camp', 'camping'], icon: '/assets/images/icons/campfire.svg' },
  {
    keywords: ['motorcycle', 'bike ride'],
    icon: '/assets/images/icons/motorcycle.svg',
  },
  { keywords: ['tour'], icon: FaMap },
  { keywords: ['visit', 'see'], icon: MapPin },
];

/**
 * Get icon component or SVG path for an activity label
 * @param label - Activity label
 * @param iconName - Optional icon name from API
 * @returns React Icons component, Lucide icon component, SVG path string, or null
 */
export function getActivityIcon(
  label: string,
  iconName?: string | null
):
  | IconType
  | React.ComponentType<{ size?: number; className?: string; color?: string }>
  | string
  | null {
  if (!label) {
    console.log('❌ [getActivityIcon] No label provided');
    return null;
  }

  const normalizedLabel = label.toLowerCase().trim();
  console.log('🔍 [getActivityIcon] Looking for icon:', {
    label,
    normalizedLabel,
    iconName,
  });

  // First, try exact match with iconName if provided
  if (iconName) {
    const normalizedIconName = iconName.toLowerCase().trim();
    if (activityIconMap[normalizedIconName]) {
      console.log(
        '✅ [getActivityIcon] Found exact match by iconName:',
        normalizedIconName
      );
      return activityIconMap[normalizedIconName];
    }
  }

  // Try exact match with label
  if (activityIconMap[normalizedLabel]) {
    console.log(
      '✅ [getActivityIcon] Found exact match by label:',
      normalizedLabel
    );
    return activityIconMap[normalizedLabel];
  }

  // Try partial match
  for (const { keywords, icon } of partialMatchMap) {
    if (keywords.some(keyword => normalizedLabel.includes(keyword))) {
      console.log('✅ [getActivityIcon] Found partial match:', {
        label: normalizedLabel,
        matchedKeywords: keywords.filter(k => normalizedLabel.includes(k)),
      });
      return icon;
    }
  }

  // Default fallback - using Lucide Zap icon for activities
  console.log('⚠️ [getActivityIcon] No match found, using default fallback');
  return Zap;
}

/**
 * Render an activity icon as a React component
 * @param label - Activity label
 * @param iconName - Optional icon name from API
 * @param size - Icon size (default: 24)
 * @param className - Additional CSS classes
 * @param color - Icon color (default: 'currentColor')
 * @returns React element or null
 */
export function renderActivityIcon(
  label: string,
  iconName?: string | null,
  size: number = 24,
  className?: string,
  color: string = 'currentColor'
): React.ReactElement | string | null {
  const iconResult = getActivityIcon(label, iconName);

  if (!iconResult) return null;

  // If it's a string (SVG path), return it directly - useUnifiedIcon will handle it
  if (typeof iconResult === 'string') {
    return iconResult;
  }

  // Otherwise it's a component (IconType or Lucide component)
  const IconComponent = iconResult;
  const props: {
    size?: number;
    className?: string;
    style?: { color: string };
    color?: string;
  } = {
    size,
    className,
    style: { color },
    color, // For react-icons compatibility (will be ignored by Lucide)
  };

  return <IconComponent {...props} />;
}

/**
 * Get all available icon names (for debugging/testing)
 */
export function getAvailableIcons(): string[] {
  return Object.keys(activityIconMap);
}
